import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRefresh} from "@fortawesome/free-solid-svg-icons/faRefresh";
import {
    formatRuntimeDate,
    getGoogleAdsAccountLabel,
    getGoogleAdsAttentionBadgeLabel,
    getGoogleAdsPartialSuccessfulAccounts,
    getGoogleAdsPartialUnavailableAccounts,
    getGoogleAdsUnavailableReason,
    getRuntimeErrorMessage,
    hasGoogleAdsPartialAccountAttention,
    hasGoogleAdsPartialAccountSnapshot,
} from "./runtimeStatusUtils.js";
import BrandLogo from "../../utils/brands/BrandLogo.jsx";
import {useBrand} from "../../utils/brands/branding.js";
import Badge from "../Badge.jsx";
import {useIsAdministrator} from "../auth/rolesAndPermissions.js";

function RuntimeInfoLine({label, value, danger = false}) {
    if (!value) {
        return null;
    }

    return (
        <div className={danger ? "text-rose-600 dark:text-rose-300" : undefined}>
            <span className={"font-bold text-slate-700 dark:text-slate-300"}>{label}: </span>
            <span>{value}</span>
        </div>
    );
}

export default function IntegrationRuntimeItem({
    integration,
    compact = false,
}) {
    const {t} = useTranslation();
    const isAdministrator = useIsAdministrator();
    const lastFetchAt = formatRuntimeDate(integration.lastSuccessfulFetchAt);
    const accountName = integration?.accountName ?? null;
    const brand = useBrand(integration.integrationName);
    const isFetching = integration.status === "running" && integration.phase === "fetching";
    const isWaitingForPublish = integration.status === "running" && integration.phase === "waiting_for_publish";
    const hasPartialGoogleAdsAttention = hasGoogleAdsPartialAccountAttention(integration);
    const hasPartialGoogleAdsSnapshot = hasGoogleAdsPartialAccountSnapshot(integration);
    const partialGoogleAdsSuccessfulAccounts = getGoogleAdsPartialSuccessfulAccounts(integration);
    const partialGoogleAdsUnavailableAccounts = getGoogleAdsPartialUnavailableAccounts(integration);
    const runtimeError = integration.errorCode ? getRuntimeErrorMessage(t, integration.errorCode) : null;
    const attention = integration.attentionCode === "bigquery-daily-limit-exhausted"
        ? t("runtime.integration.bigquery_daily_limit_exhausted", {defaultValue: "Denní limit BigQuery byl vyčerpán. Data se znovu začnou stahovat zítra."})
        : integration.attentionCode === "bigquery-monthly-limit-exhausted"
            ? t("runtime.integration.bigquery_monthly_limit_exhausted", {defaultValue: "Měsíční limit BigQuery byl vyčerpán. Data se znovu začnou stahovat příští měsíc."})
            : null;

    return (
        <div className={"flex min-h-[58px] items-center rounded-lg border bg-slate-50/80 dark:bg-slate-900/40 " + (compact ? "px-3 py-2" : "px-4 py-3")}>
            <div className={"min-w-0 flex-1"}>
                <div className={"flex flex-wrap items-center gap-2"}>
                    {brand && <BrandLogo brand={brand} size={6} className={"shrink-0"} />}
                    <div className={"font-semibold text-black dark:text-slate-100 mr-auto"}>
                        {integration.integrationName}
                    </div>

                    {!isFetching && !isWaitingForPublish && (
                        <Badge color={hasPartialGoogleAdsAttention && integration.status !== "failed" ? "yellow" : integration.ready && integration.status !== "failed" ? "emerald" : "rose"} className={"ml-auto"}>
                            {hasPartialGoogleAdsAttention && integration.status !== "failed"
                                ? getGoogleAdsAttentionBadgeLabel(t, integration.successfulAccounts ?? [])
                                : integration.ready && integration.status !== "failed"
                                ? t("runtime.integration.connection_active", {defaultValue: "Aktivní"})
                                : integration.status === "failed"
                                    ? t("runtime.integration.failed", {defaultValue: "Chyba"})
                                    : t("runtime.integration.connection_inactive", {defaultValue: "Neaktivní"})}
                        </Badge>
                    )}

                    {isFetching && (
                        <Badge color={"yellow"} className={"gap-1.5 ml-auto"}>
                            <FontAwesomeIcon icon={faRefresh} className={"animate-spin"} />
                            <span>{t("runtime.integration.fetching_now", {defaultValue: "Stahuji data"})}</span>
                        </Badge>
                    )}

                    {isWaitingForPublish && (
                        <Badge color={"yellow"} className={"gap-1.5 ml-auto"}>
                            <FontAwesomeIcon icon={faRefresh} className={"animate-spin"} />
                            <span>{t("runtime.integration.waiting_for_publish", {defaultValue: "Čeká na zpracování projektu"})}</span>
                        </Badge>
                    )}
                </div>

                <div className={"mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300"}>
                    <RuntimeInfoLine
                        label={t("runtime.integration.resources", {defaultValue: "Aktivní zdroje"})}
                        value={accountName}
                    />
                    <RuntimeInfoLine
                        label={t("runtime.integration.last_update", {defaultValue: "Aktualizováno"})}
                        value={lastFetchAt}
                    />
                    <RuntimeInfoLine
                        label={t("runtime.integration.error", {defaultValue: "Chyba"})}
                        value={runtimeError}
                        danger={true}
                    />
                    {isAdministrator && integration.error && (
                        <details className={"rounded-md border border-violet-200 bg-violet-50/70 px-2.5 py-2 text-slate-700 dark:border-violet-800 dark:bg-violet-950/30 dark:text-slate-200"}>
                            <summary className={"cursor-pointer font-semibold text-violet-700 dark:text-violet-300"}>
                                {integration.errorMapped
                                    ? t("runtime.integration.technical_error_mapped", {defaultValue: "Technický detail · rozpoznaná chyba"})
                                    : t("runtime.integration.technical_error_unmapped", {defaultValue: "Technický detail · chybí mapování"})}
                            </summary>
                            <div className={"mt-2 break-words font-mono text-[11px] leading-relaxed"}>{integration.error}</div>
                        </details>
                    )}
                    {attention && (
                        <div className={"rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"}>
                            {attention}
                        </div>
                    )}
                    {hasPartialGoogleAdsSnapshot && (
                        <div className={"space-y-2"}>
                            <div className={"rounded-md border border-rose-300 bg-rose-50 px-2.5 py-2 text-rose-900 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-100"}>
                                <div className={"font-semibold"}>
                                    {t("runtime.integration.google_ads_partial.not_downloaded_title", {defaultValue: "Data nebyla stažena"})}
                                </div>
                                <ul className={"mt-1 list-disc space-y-0.5 pl-5"}>
                                    {partialGoogleAdsUnavailableAccounts.map((account) => (
                                        <li key={account.id}>
                                            <span className={"font-semibold"}>{getGoogleAdsAccountLabel(account)}</span>
                                            {" — "}{getGoogleAdsUnavailableReason(t, account.reason)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {partialGoogleAdsSuccessfulAccounts.length > 0 && (
                                <div className={"rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-2 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"}>
                                    <div className={"font-semibold"}>
                                        {t("runtime.integration.google_ads_partial.downloaded_title", {defaultValue: "Data byla stažena"})}
                                    </div>
                                    <ul className={"mt-1 list-disc space-y-0.5 pl-5"}>
                                        {partialGoogleAdsSuccessfulAccounts.map((account) => (
                                            <li key={account.id}>
                                                <span className={"font-semibold"}>{getGoogleAdsAccountLabel(account)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
